import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import api from '../api/axios'
import { Users, X, Mail, Shield, Clock, Plus, Trash2, Pencil, Search, Sun, Moon, XCircle, Send, Calendar, Download } from 'lucide-react'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { useAuth } from '../context/AuthContext'

function StaffDetailModal({ member, onClose, onDeleted, onUpdated, currentUser }: {
  member: any, onClose: () => void, onDeleted: () => void, onUpdated: () => void, currentUser: any
}) {
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const [activeTab, setActiveTab] = useState<'details' | 'hr-info'>('details')
  const [hrInfo, setHrInfo] = useState<any>(null)
  const [hrInfoLoading, setHrInfoLoading] = useState(false)
  const [hrInfoSaving, setHrInfoSaving] = useState(false)
  const [hrInfoSaved, setHrInfoSaved] = useState(false)
  const [hrForm, setHrForm] = useState({
    employeeId: '', aadhaarNumber: '', bloodGroup: '', gender: '', personalEmail: '',
    altPhoneNumber: '', emergencyContactName: '', emergencyRelationship: '',
    emergencyContactPhone: '', permanentAddress: '', contactAddress: '',
    bankAccountNumber: '', bankIfscCode: '', bankBranchName: ''
  })
  const [form, setForm] = useState({
    name: member.name,
    email: member.email || '',
    roleName: member.roleName,
    status: member.status,
    dateOfBirth: member.dateOfBirth ? member.dateOfBirth.split('T')[0] : '',
    joiningDate: member.joiningDate ? member.joiningDate.split('T')[0] : ''
  })
  const [roles, setRoles] = useState<any[]>([])
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState('')
  const [allClients, setAllClients] = useState<any[]>([])
  const [assignedClients, setAssignedClients] = useState<string[]>([])
  const [newPassword, setNewPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }))

  useEffect(() => {
    if (activeTab === 'hr-info' && !hrInfo) {
      setHrInfoLoading(true)
      api.get(`/staff/${member.id}/hr-info`).then(res => {
        setHrInfo(res.data)
        setHrForm({
          employeeId: res.data.employeeId || '',
          aadhaarNumber: res.data.aadhaarNumber || '',
          bloodGroup: res.data.bloodGroup || '',
          gender: res.data.gender || '',
          personalEmail: res.data.personalEmail || '',
          altPhoneNumber: res.data.altPhoneNumber || '',
          emergencyContactName: res.data.emergencyContactName || '',
          emergencyRelationship: res.data.emergencyRelationship || '',
          emergencyContactPhone: res.data.emergencyContactPhone || '',
          permanentAddress: res.data.permanentAddress || '',
          contactAddress: res.data.contactAddress || '',
          bankAccountNumber: res.data.bankAccountNumber || '',
          bankIfscCode: res.data.bankIfscCode || '',
          bankBranchName: res.data.bankBranchName || ''
        })
      }).catch(() => {}).finally(() => setHrInfoLoading(false))
    }
  }, [activeTab])

  useEffect(() => {
    api.get('/staff/roles').then(res => setRoles(res.data)).catch(() => {})
    api.get('/clients/basic').then(res => setAllClients(res.data)).catch(() => {})
    api.get(`/staff/${member.id}/clients`).then(res => setAssignedClients(res.data.map((c: any) => c.clientId))).catch(() => {})
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await api.patch(`/staff/${member.id}`, form)
      await api.put(`/staff/${member.id}/clients`, { clientIds: assignedClients })
      if (newPassword.trim()) {
        await api.patch(`/staff/${member.id}/reset-password`, { newPassword })
      }
      onUpdated()
      setEditing(false)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.patch(`/staff/${member.id}`, { status: 'inactive' })
      onDeleted()
      onClose()
    } catch {
      setDeleting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm animate-backdrop-in" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 dark:border-[#1e3a5f] flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-navy-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-display font-bold text-lg">{member.name[0]}</span>
            </div>
            <div>
              <h2 className="font-display font-bold text-navy-900 dark:text-white text-lg">{member.name}</h2>
              {member.username && (
                <span className="font-mono text-xs text-orange-500">@{member.username}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} className="text-gray-400 hover:text-navy-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <Pencil size={15} />
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <X size={15} />
            </button>
          </div>
        </div>

        {!editing && (
          <div className="flex border-b border-gray-100 dark:border-[#1e3a5f]">
            {(['details', 'hr-info'] as const).map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px capitalize ${activeTab === tab ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-navy-700 dark:hover:text-white'}`}>
                {tab === 'hr-info' ? 'HR Info' : 'Details'}
              </button>
            ))}
          </div>
        )}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-100">{error}</div>}

          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="label">Full Name</label>
                <input className="input" value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div>
                <label className="label">Email <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
                <input className="input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="priya@studio.com" />
              </div>
              <div>
                <label className="label">Role</label>
                <select className="input" value={form.roleName} onChange={e => set('roleName', e.target.value)}>
                  {roles.filter(r => currentUser?.roleName === 'admin' || r.roleName !== 'admin').map(r => (
                    <option key={r.roleName} value={r.roleName}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.status} onChange={e => set('status', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div>
                <label className="label">Date of Birth</label>
                <input className="input" type="date" value={form.dateOfBirth} onChange={e => set('dateOfBirth', e.target.value)} />
              </div>
              <div>
                <label className="label">Joining Date</label>
                <input className="input" type="date" value={form.joiningDate} onChange={e => set('joiningDate', e.target.value)} />
              </div>
              <div>
                <label className="label">New Password <span className="text-gray-400 font-normal normal-case">(leave blank to keep current)</span></label>
                <div className="relative">
                  <input className="input pr-10" type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                  </button>
                </div>
              </div>
              {['artist', 'selfqc', 'qc', 'tl'].includes(form.roleName) && allClients.length > 0 && (
                <div>
                  <label className="label">Assigned Clients <span className="text-gray-400 font-normal normal-case">(leave empty for all)</span></label>
                  <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto space-y-1.5">
                    {allClients.map((c: any) => (
                      <label key={c.clientId} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox"
                          checked={assignedClients.includes(c.clientId)}
                          onChange={e => setAssignedClients(prev =>
                            e.target.checked ? [...prev, c.clientId] : prev.filter(id => id !== c.clientId)
                          )}
                          className="w-4 h-4 rounded border-gray-300 text-orange-500" />
                        <span className="text-sm text-navy-700">{c.abbreviation || c.clientCode}</span>
                            {currentUser?.roleName === 'admin' && (
                              <span className="text-xs text-gray-400">{c.companyName}</span>
                            )}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => setEditing(false)} className="btn-ghost flex-1">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="btn-primary flex-1 disabled:opacity-50">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          ) : activeTab === 'hr-info' ? (
            <div className="space-y-4">
              {hrInfoLoading ? (
                <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
              ) : (
                <>
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 dark:border-orange-900/30 pb-2">Personal</p>
                    <div>
                      <label className="label">Employee ID</label>
                      <input className="input text-sm" value={hrForm.employeeId} onChange={e => setHrForm(f => ({ ...f, employeeId: e.target.value }))} placeholder="e.g. INK-001" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label">Gender</label>
                        <select className="input text-sm" value={hrForm.gender} onChange={e => setHrForm(f => ({ ...f, gender: e.target.value }))}>
                          <option value="">Select</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Blood Group</label>
                        <select className="input text-sm" value={hrForm.bloodGroup} onChange={e => setHrForm(f => ({ ...f, bloodGroup: e.target.value }))}>
                          <option value="">Select</option>
                          {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => <option key={bg} value={bg}>{bg}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label">Aadhaar Number</label>
                      <input className="input text-sm" value={hrForm.aadhaarNumber} onChange={e => setHrForm(f => ({ ...f, aadhaarNumber: e.target.value }))} placeholder="XXXX XXXX XXXX" />
                    </div>
                    <div>
                      <label className="label">Personal Email</label>
                      <input className="input text-sm" type="email" value={hrForm.personalEmail} onChange={e => setHrForm(f => ({ ...f, personalEmail: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Alt Phone</label>
                      <input className="input text-sm" value={hrForm.altPhoneNumber} onChange={e => setHrForm(f => ({ ...f, altPhoneNumber: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Emergency Contact</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label">Name</label>
                        <input className="input text-sm" value={hrForm.emergencyContactName} onChange={e => setHrForm(f => ({ ...f, emergencyContactName: e.target.value }))} />
                      </div>
                      <div>
                        <label className="label">Relationship</label>
                        <select className="input text-sm" value={hrForm.emergencyRelationship} onChange={e => setHrForm(f => ({ ...f, emergencyRelationship: e.target.value }))}>
                          <option value="">Select</option>
                          <option value="Parent">Parent</option>
                          <option value="Spouse">Spouse</option>
                          <option value="Sibling">Sibling</option>
                          <option value="Friend">Friend</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label">Phone</label>
                      <input className="input text-sm" value={hrForm.emergencyContactPhone} onChange={e => setHrForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Address</p>
                    <div>
                      <label className="label">Permanent Address</label>
                      <textarea className="input resize-none text-sm" rows={2} value={hrForm.permanentAddress} onChange={e => setHrForm(f => ({ ...f, permanentAddress: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Contact Address <span className="text-gray-400 font-normal">(if different)</span></label>
                      <textarea className="input resize-none text-sm" rows={2} value={hrForm.contactAddress} onChange={e => setHrForm(f => ({ ...f, contactAddress: e.target.value }))} />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-orange-500 uppercase tracking-wide border-b border-orange-100 pb-2">Bank Details</p>
                    <div>
                      <label className="label">Account Number</label>
                      <input className="input text-sm" value={hrForm.bankAccountNumber} onChange={e => setHrForm(f => ({ ...f, bankAccountNumber: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="label">IFSC Code</label>
                        <input className="input text-sm uppercase" value={hrForm.bankIfscCode} onChange={e => setHrForm(f => ({ ...f, bankIfscCode: e.target.value.toUpperCase() }))} maxLength={11} />
                      </div>
                      <div>
                        <label className="label">Branch Name</label>
                        <input className="input text-sm" value={hrForm.bankBranchName} onChange={e => setHrForm(f => ({ ...f, bankBranchName: e.target.value }))} />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2">
                    {hrInfoSaved && <span className="text-xs text-green-500">✓ Saved</span>}
                    <button onClick={async () => {
                      setHrInfoSaving(true)
                      try {
                        await api.patch(`/staff/${member.id}/hr-info`, hrForm)
                        setHrInfoSaved(true)
                        setTimeout(() => setHrInfoSaved(false), 2000)
                      } catch { }
                      setHrInfoSaving(false)
                    }} disabled={hrInfoSaving} className="btn-primary flex-1 disabled:opacity-50 text-sm">
                      {hrInfoSaving ? 'Saving...' : 'Save HR Info'}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              {member.email && (
                <div className="flex items-center gap-3 text-sm">
                  <Mail size={15} className="text-gray-400" />
                  <span className="text-navy-700">{member.email}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Shield size={15} className="text-gray-400" />
                <span className="bg-orange-50 text-orange-600 px-2.5 py-0.5 rounded-full text-xs font-medium">{member.roleLabel}</span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${member.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                  {member.status}
                </span>
              </div>
              <div className="flex items-center gap-3 text-sm">
                <Clock size={15} className="text-gray-400" />
                <span className="text-navy-700">
                  {member.lastLoginAt ? `Last login: ${new Date(member.lastLoginAt).toLocaleString()}` : 'Never logged in'}
                </span>
              </div>
              <div className="pt-2 border-t border-gray-100 dark:border-[#1e3a5f] space-y-1.5">
                <p className="text-xs text-gray-400">Member since {new Date(member.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                {member.joiningDate && (
                  <p className="text-xs text-gray-400">🗓 Joined: {new Date(member.joiningDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                )}
                {member.dateOfBirth && (
                  <p className="text-xs text-gray-400">🎂 Birthday: {new Date(member.dateOfBirth).toLocaleDateString('en-IN', { day: 'numeric', month: 'long' })}</p>
                )}
              </div>

              {/* Delete section */}
              <div className="border-t border-gray-100 dark:border-[#1e3a5f] pt-3">
                {member.status === 'inactive' ? (
                  <div className="space-y-2">
                    <button onClick={async () => {
                      try {
                        await api.patch(`/staff/${member.id}`, { status: 'active' })
                        onUpdated()
                        onClose()
                      } catch { }
                    }} className="flex items-center gap-2 text-green-500 hover:text-green-600 text-sm font-medium transition-colors">
                      ✓ Reactivate staff member
                    </button>
                    {confirmDelete ? (
                      <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 space-y-3">
                        <p className="text-sm text-red-700 dark:text-red-300 font-medium">Permanently delete {member.name}?</p>
                        <p className="text-xs text-red-500">This cannot be undone. All their data will be removed.</p>
                        <div className="flex gap-2">
                          <button onClick={() => setConfirmDelete(false)} className="btn-ghost text-xs py-1.5 flex-1">Cancel</button>
                          <button onClick={async () => {
                            setDeleting(true)
                            try {
                              await api.delete(`/staff/${member.id}`)
                              onDeleted()
                              onClose()
                            } catch { setDeleting(false) }
                          }} disabled={deleting}
                            className="bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 px-3 rounded-xl flex-1 font-medium transition-colors disabled:opacity-50">
                            {deleting ? 'Deleting...' : 'Yes, delete permanently'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-2 text-red-400 hover:text-red-600 text-sm font-medium transition-colors">
                        <Trash2 size={14} /> Delete permanently
                      </button>
                    )}
                  </div>
                ) : !confirmDelete ? (
                  <button onClick={() => setConfirmDelete(true)} className="flex items-center gap-2 text-red-400 hover:text-red-600 text-sm font-medium transition-colors">
                    <Trash2 size={14} /> Deactivate staff member
                  </button>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 space-y-3">
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">Deactivate {member.name}?</p>
                    <p className="text-xs text-red-500 dark:text-red-400">They will no longer be able to log in.</p>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirmDelete(false)} className="btn-ghost text-xs py-1.5 flex-1">Cancel</button>
                      <button onClick={handleDelete} disabled={deleting}
                        className="bg-red-500 hover:bg-red-600 text-white text-xs py-1.5 px-3 rounded-xl flex-1 font-medium transition-colors disabled:opacity-50">
                        {deleting ? 'Deactivating...' : 'Yes, deactivate'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function NewStaffModal({ onClose, onCreated, currentUser }: { onClose: () => void, onCreated: () => void, currentUser: any }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])
  const [form, setForm] = useState({ name: '', email: '', username: '', roleName: 'artist', password: '' })
  const [roles, setRoles] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showNewPwd, setShowNewPwd] = useState(false)

  const set = (key: string, value: string) => setForm(f => ({ ...f, [key]: value }))

  useEffect(() => {
    api.get('/staff/roles').then(res => setRoles(res.data)).catch(() => {})
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.username) { setError('Username is required'); return }
    setError('')
    setLoading(true)
    try {
      await api.post('/staff', form)
      onCreated()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create staff member')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-modal w-full max-w-md animate-modal-in">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-display font-bold text-navy-900 text-xl">New Staff Member</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-100 text-red-600 text-sm px-4 py-3 rounded-xl">{error}</div>
          )}

          <div>
            <label className="label">Full Name *</label>
            <input className="input" autoComplete="off" value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Priya Sharma" />
          </div>

          <div>
            <label className="label">Username *</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
              <input
                className="input pl-7"
                autoComplete="off"
                value={form.username}
                onChange={e => set('username', e.target.value.toLowerCase().replace(/\s+/g, '.'))}
                required
                placeholder="priya.sharma"
              />
            </div>
          </div>

          <div>
            <label className="label">Email <span className="text-gray-400 font-normal normal-case">(optional)</span></label>
            <input className="input" type="email" autoComplete="off" value={form.email} onChange={e => set('email', e.target.value)} placeholder="priya@studio.com" />
          </div>

          <div>
            <label className="label">Role *</label>
            <select className="input" value={form.roleName} onChange={e => set('roleName', e.target.value)}>
              {roles.filter(r => currentUser?.roleName === 'admin' || r.roleName !== 'admin').map(r => (
                <option key={r.roleName} value={r.roleName}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">Password *</label>
            <div className="relative">
              <input className="input pr-10" type={showNewPwd ? 'text' : 'password'} autoComplete="new-password" value={form.password} onChange={e => set('password', e.target.value)} required placeholder="••••••••" />
              <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNewPwd ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary flex-1 disabled:opacity-50">
              {loading ? 'Creating...' : 'Create Staff'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ShiftSchedule() {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date()
    const day = d.getDay()
    const diff = day === 0 ? -6 : 1 - day
    d.setDate(d.getDate() + diff)
    return d.toLocaleDateString('en-CA') // YYYY-MM-DD in local timezone
  })
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [copying, setCopying] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'balance'>('grid')
  const [selectedDay, setSelectedDay] = useState(0) // 0-5 for Mon-Sat

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const shiftTypes = ['day', 'night', 'off']
  const shiftColors: Record<string, string> = {
    day: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
    night: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400',
    off: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  }
  const shiftIcons: Record<string, any> = { 
    day: <Sun size={14} className="text-orange-500" />, 
    night: <Moon size={14} className="text-indigo-400" />, 
    off: <XCircle size={14} className="text-red-400" /> 
  }

  const fetchShifts = async () => {
    setLoading(true)
    try {
      const res = await api.get(`/shifts?weekStart=${weekStart}`)
      console.log('Shifts response:', res.data)
      setData(res.data)
    } catch (e: any) {
      console.error('Shifts fetch error:', e.response?.status, e.response?.data || e.message)
    }
    setLoading(false)
  }

  useEffect(() => { fetchShifts() }, [weekStart])

  const getDates = () => {
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(weekStart + 'T12:00:00')
      d.setDate(d.getDate() + i)
      return d.toLocaleDateString('en-CA')
    })
  }

  const cycleShift = async (staffId: string, date: string, current: string | undefined) => {
    setSaving(true)
    try {
      if (!current) {
        // No shift — set to day
        await api.post('/shifts', { staffId, date, shiftType: 'day' })
        setData((prev: any) => ({ ...prev, staff: prev.staff.map((s: any) =>
          s.id === staffId ? { ...s, shifts: { ...s.shifts, [date]: 'day' } } : s
        )}))
      } else if (current === 'day') {
        await api.post('/shifts', { staffId, date, shiftType: 'night' })
        setData((prev: any) => ({ ...prev, staff: prev.staff.map((s: any) =>
          s.id === staffId ? { ...s, shifts: { ...s.shifts, [date]: 'night' } } : s
        )}))
      } else if (current === 'night') {
        await api.post('/shifts', { staffId, date, shiftType: 'off' })
        setData((prev: any) => ({ ...prev, staff: prev.staff.map((s: any) =>
          s.id === staffId ? { ...s, shifts: { ...s.shifts, [date]: 'off' } } : s
        )}))
      } else {
        // off → clear/null
        await api.delete(`/shifts/${staffId}/${date}`)
        setData((prev: any) => ({ ...prev, staff: prev.staff.map((s: any) => {
          if (s.id !== staffId) return s
          const newShifts = { ...s.shifts }
          delete newShifts[date]
          return { ...s, shifts: newShifts }
        })}))
      }
    } catch {}
    setSaving(false)
  }

  const prevWeek = () => {
    const d = new Date(weekStart + 'T12:00:00')
    d.setDate(d.getDate() - 7)
    setWeekStart(d.toLocaleDateString('en-CA'))
  }

  const nextWeek = () => {
    const d = new Date(weekStart + 'T12:00:00')
    d.setDate(d.getDate() + 7)
    setWeekStart(d.toLocaleDateString('en-CA'))
  }

  const dates = getDates()
  const weekLabel = new Date(weekStart + 'T12:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
  const weekEnd = new Date(weekStart + 'T12:00:00')
  weekEnd.setDate(weekEnd.getDate() + 5)
  const weekEndLabel = weekEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={prevWeek} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="font-semibold text-navy-800 dark:text-white text-sm">{weekLabel} — {weekEndLabel}</span>
          <button onClick={nextWeek} className="p-1.5 rounded-lg border border-gray-200 dark:border-[#1e3a5f] hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          {data?.isPublished && (
            <span className="text-xs bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">✓ Published</span>
          )}
          {/* View toggle */}
          <div className="flex bg-gray-100 dark:bg-[#112240] rounded-xl p-1 gap-1">
            <button onClick={() => setViewMode('grid')}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
              Grid
            </button>
            <button onClick={() => setViewMode('balance')}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${viewMode === 'balance' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
              Balance
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={async () => {
            setCopying(true)
            try { await api.post('/shifts/copy-previous', { weekStart }); fetchShifts() } catch {}
            setCopying(false)
          }} disabled={copying} className="text-xs border border-gray-200 dark:border-[#1e3a5f] text-gray-500 px-3 py-1.5 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors disabled:opacity-50">
            {copying ? 'Copying...' : 'Copy Previous Week'}
          </button>
          <button onClick={async () => {
            setPublishing(true)
            try { await api.post('/shifts/publish', { weekStart }); fetchShifts() } catch {}
            setPublishing(false)
          }} disabled={publishing} className="text-xs bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-xl transition-colors disabled:opacity-50 font-medium">
            {publishing ? 'Publishing...' : <><Send size={13} /> Publish Schedule</>}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="px-6 py-2 border-b border-gray-50 dark:border-[#1a3050] flex items-center gap-4">
        {Object.entries(shiftIcons).map(([type, icon]) => (
          <span key={type} className="text-xs text-gray-400 flex items-center gap-1">
            {icon} <span className="capitalize">{type === 'half' ? 'Half Day' : type}</span>
          </span>
        ))}
        <span className="text-xs text-gray-300 dark:text-gray-600">· Click cell to cycle</span>
      </div>

      {/* Schedule grid */}
      {loading ? (
        <div className="p-6 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="skeleton h-10 rounded-xl" />)}
        </div>
      ) : viewMode === 'balance' ? (
        <div className="p-4 space-y-4">
          {/* Day selector + Copy button */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {dates.map((date, i) => (
              <button key={date} onClick={() => setSelectedDay(i)}
                className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${selectedDay === i ? 'bg-orange-500 text-white' : 'bg-gray-100 dark:bg-[#112240] text-gray-500 hover:text-navy-700 dark:hover:text-white'}`}>
                <span className="block text-xs opacity-70">{days[i]}</span>
                <span>{new Date(date).getDate()}</span>
              </button>
            ))}
            <button onClick={async () => {
              const firstDate = dates[0]
              const allStaff = data?.staff || []
              setSaving(true)
              try {
                await Promise.all(
                  allStaff.flatMap((s: any) =>
                    dates.slice(1).map((d: string) => {
                      const shift = s.shifts[firstDate]
                      if (shift) return api.post('/shifts', { staffId: s.id, date: d, shiftType: shift })
                      return Promise.resolve()
                    })
                  )
                )
                fetchShifts()
              } catch {}
              setSaving(false)
            }} disabled={saving}
              className="flex-shrink-0 ml-2 px-3 py-2 rounded-xl text-xs font-medium bg-navy-100 dark:bg-[#112240] text-navy-600 dark:text-gray-300 border border-navy-200 dark:border-[#1e3a5f] hover:bg-orange-50 dark:hover:bg-orange-900/10 hover:text-orange-600 transition-colors disabled:opacity-50">
              Copy {days[0]} → Rest
            </button>
          </div>

          {/* Day/Night panels */}
          {(() => {
            const date = dates[selectedDay]
            const dayStaff = data?.staff?.filter((s: any) => s.shifts[date] === 'day') || []
            const nightStaff = data?.staff?.filter((s: any) => s.shifts[date] === 'night') || []
            const offStaff = data?.staff?.filter((s: any) => s.shifts[date] === 'off') || []
            const unsetStaff = data?.staff?.filter((s: any) => !s.shifts[date]) || []

            const handleDrop = async (staffId: string, shiftType: string) => {
              setSaving(true)
              try {
                if (shiftType === 'unset') {
                  await api.delete(`/shifts/${staffId}/${date}`)
                } else {
                  await api.post('/shifts', { staffId, date, shiftType })
                }
                fetchShifts()
              } catch {}
              setSaving(false)
            }

            const StaffCard = ({ s, currentShift }: { s: any, currentShift: string | undefined }) => (
              <div
                draggable
                onDragStart={(e: React.DragEvent) => e.dataTransfer.setData('staffId', s.id)}
                onClick={() => cycleShift(s.id, date, currentShift)}
                className="flex items-center gap-2 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-xl px-3 py-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-all">
                <div className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-[#112240] flex items-center justify-center flex-shrink-0">
                  <span className="text-gray-600 dark:text-gray-300 text-xs font-bold">{s.name[0]}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-navy-800 dark:text-white truncate">{s.name.split(' ')[0]}</p>
                  <p className="text-[10px] text-gray-400 capitalize">{s.roleName}</p>
                </div>
              </div>
            )

            return (
              <div className="space-y-4">
                {/* Unassigned pool */}
                {unsetStaff.length > 0 && (
                  <div className="bg-gray-50 dark:bg-[#0d1e35] border border-gray-200 dark:border-[#1e3a5f] rounded-2xl p-4"
                    onDragOver={(e: React.DragEvent) => e.preventDefault()}
                    onDrop={(e: React.DragEvent) => { e.preventDefault(); handleDrop(e.dataTransfer.getData('staffId'), 'unset') }}>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">❓ Unassigned — {unsetStaff.length} staff</p>
                    <div className="flex flex-wrap gap-2">
                      {unsetStaff.map((s: any) => <StaffCard key={s.id} s={s} currentShift={undefined} />)}
                    </div>
                  </div>
                )}

                {/* Day & Night columns */}
                <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 rounded-2xl p-4"
                    onDragOver={(e: React.DragEvent) => e.preventDefault()}
                    onDrop={(e: React.DragEvent) => { e.preventDefault(); handleDrop(e.dataTransfer.getData('staffId'), 'day') }}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-orange-700 dark:text-orange-400 flex items-center gap-1"><Sun size={15} /> Day Shift</h3>
                      <span className="text-xs bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full font-bold">{dayStaff.length}</span>
                    </div>
                    <div className="space-y-2 min-h-[80px]">
                      {dayStaff.map((s: any) => <StaffCard key={s.id} s={s} currentShift="day" />)}
                      {dayStaff.length === 0 && <p className="text-xs text-orange-300 dark:text-orange-800 text-center py-6">Drop staff here</p>}
                    </div>
                  </div>

                <div className="bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-900/30 rounded-2xl p-4"
                    onDragOver={(e: React.DragEvent) => e.preventDefault()}
                    onDrop={(e: React.DragEvent) => { e.preventDefault(); handleDrop(e.dataTransfer.getData('staffId'), 'night') }}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-indigo-700 dark:text-indigo-400 flex items-center gap-1"><Moon size={15} /> Night Shift</h3>
                      <span className="text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-2 py-0.5 rounded-full font-bold">{nightStaff.length}</span>
                    </div>
                    <div className="space-y-2 min-h-[80px]">
                      {nightStaff.map((s: any) => <StaffCard key={s.id} s={s} currentShift="night" />)}
                      {nightStaff.length === 0 && <p className="text-xs text-indigo-300 dark:text-indigo-800 text-center py-6">Drop staff here</p>}
                    </div>
                  </div>
                </div>

                {/* Off */}
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/30 rounded-2xl p-4"
                  onDragOver={(e: React.DragEvent) => e.preventDefault()}
                  onDrop={(e: React.DragEvent) => { e.preventDefault(); handleDrop(e.dataTransfer.getData('staffId'), 'off') }}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-red-600 dark:text-red-400 flex items-center gap-1"><XCircle size={15} /> Off</h3>
                    <span className="text-xs bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 px-2 py-0.5 rounded-full font-bold">{offStaff.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[40px]">
                    {offStaff.map((s: any) => <StaffCard key={s.id} s={s} currentShift="off" />)}
                    {offStaff.length === 0 && <p className="text-xs text-red-300 dark:text-red-900 py-1">Drop staff here</p>}
                  </div>
                </div>
              </div>
            )
          })()}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 dark:bg-[#0d1e35]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide w-40">Staff</th>
                {dates.map((date, i) => (
                  <th key={date} className="px-3 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">
                    <div>{days[i]}</div>
                    <div className="text-[10px] font-normal text-gray-300">{new Date(date).getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-[#1a3050]">
              {data?.staff?.map((member: any) => (
                <tr key={member.id} className="hover:bg-gray-50/50 dark:hover:bg-[#1a3050]/50 transition-colors">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg bg-navy-100 dark:bg-[#1a3050] flex items-center justify-center flex-shrink-0">
                        <span className="text-navy-600 dark:text-gray-300 text-[10px] font-bold">{member.name[0]}</span>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-navy-800 dark:text-white truncate max-w-[100px] block">{member.name.split(' ')[0]}</span>
                        <div className="flex gap-1 mt-0.5">
                          {['day', 'night', 'off'].map(type => (
                            <button key={type} onClick={async () => {
                              setSaving(true)
                              try {
                                await Promise.all(getDates().map((date: string) =>
                                  api.post('/shifts', { staffId: member.id, date, shiftType: type })
                                ))
                                fetchShifts()
                              } catch {}
                              setSaving(false)
                            }} disabled={saving}
                              className="text-[9px] px-1 py-0.5 rounded border transition-colors disabled:opacity-50 text-gray-400 border-gray-200 hover:border-orange-300 hover:text-orange-500 dark:border-[#1e3a5f]">
                              {type === 'day' ? <Sun size={13} className="text-orange-500" /> : type === 'night' ? <Moon size={13} className="text-indigo-400" /> : <XCircle size={13} className="text-red-400" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </td>
                  {dates.map(date => {
                    const shift = member.shifts[date] || 'working'
                    return (
                      <td key={date} className="px-3 py-3 text-center">
                        <button
                          onClick={() => cycleShift(member.id, date, member.shifts[date])}
                          disabled={saving}
                          className={`w-full px-2 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80 ${member.shifts[date] ? shiftColors[member.shifts[date]] : 'bg-gray-50 dark:bg-[#1a3050] text-gray-300'}`}
                          title={member.shifts[date] ? `Currently ${member.shifts[date]} — click to change` : 'Click to set shift'}
                        >
                          {member.shifts[date] ? shiftIcons[member.shifts[date]] : '+'}
                        </button>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function StaffPage() {
  const { user, hasPermission } = useAuth()
  const [staff, setStaff] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<any>(null)
  const [showNewForm, setShowNewForm] = useState(false)
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [filterStatus, setFilterStatus] = useState('active')
  const [artistStatus, setArtistStatus] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState<'staff' | 'schedule'>('staff')
  const [hoveredArtist, setHoveredArtist] = useState<any>(null)
  const [artistHoverPos, setArtistHoverPos] = useState({ x: 0, y: 0 })
  const [artistStats, setArtistStats] = useState<any>(null)
  const artistHoverTimeout = useRef<NodeJS.Timeout | null>(null)

  const fetchStaff = () => {
    setLoading(true)
    api.get('/staff').then(res => {
      setStaff(res.data)
      setLoading(false)
    }).catch(() => setLoading(false))
  }

  const downloadStaffXLSX = () => {
    const rows = filtered.map(m => ({
      Name: m.name,
      Username: m.username || '',
      Email: m.email || '',
      Role: m.roleName || '',
      Status: m.status || '',
      Phone: m.phone || '',
      'Joining Date': m.joiningDate ? new Date(m.joiningDate).toLocaleDateString('en-IN') : '',
      'Date of Birth': m.dateOfBirth ? new Date(m.dateOfBirth).toLocaleDateString('en-IN') : '',
    }))
    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Staff')
    XLSX.writeFile(wb, `Inkcredible_Staff_${new Date().toLocaleDateString('en-CA')}.xlsx`)
  }

  const downloadStaffPDF = async () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageW = doc.internal.pageSize.getWidth()

    // Header
    doc.setFillColor(10, 22, 40)
    doc.rect(0, 0, pageW, 22, 'F')
    try {
      const logoRes = await fetch('/Logo_Black Bg_Horizontal.png')
      const blob = await logoRes.blob()
      const logoBase64 = await new Promise<string>(resolve => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.readAsDataURL(blob)
      })
      doc.addImage(logoBase64, 'PNG', 10, 6, 36, 36 * (50 / 201))
    } catch {}
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text('Staff Directory', pageW - 10, 10, { align: 'right' })
    doc.setFontSize(8)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(180, 180, 180)
    doc.text(`Generated ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · ${filtered.length} members`, pageW - 10, 16, { align: 'right' })

    autoTable(doc, {
      startY: 28,
      head: [['Name', 'Username', 'Email', 'Role', 'Status', 'Joining Date']],
      body: filtered.map(m => [
        m.name,
        m.username || '—',
        m.email || '—',
        m.roleName || '—',
        m.status || '—',
        m.joiningDate ? new Date(m.joiningDate).toLocaleDateString('en-IN') : '—'
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [10, 22, 40], textColor: 255 },
      alternateRowStyles: { fillColor: [248, 248, 248] },
      margin: { left: 10, right: 10 },
      theme: 'grid'
    })

    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text('Inkcredible Studios · Confidential · For internal use only', pageW / 2, 287, { align: 'center' })
    doc.save(`Inkcredible_Staff_${new Date().toLocaleDateString('en-CA')}.pdf`)
  }

  const filtered = staff.filter(m => {
    const matchSearch = !search || m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.username?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase())
    const matchRole = filterRole === 'all' || m.roleName === filterRole
    const matchStatus = filterStatus === 'all' || m.status === filterStatus
    return matchSearch && matchRole && matchStatus
  })

  useEffect(() => { fetchStaff() }, [])
  useEffect(() => {
    api.get('/staff/artist-status').then(res => setArtistStatus(res.data)).catch(() => {})
    const interval = setInterval(() => {
      api.get('/staff/artist-status').then(res => setArtistStatus(res.data)).catch(() => {})
    }, 15000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy-900">Staff</h1>
          <p className="text-gray-400 mt-1">{filtered.length} of {staff.length} team members</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {hasPermission('manage_shift_schedule') && (
            <div className="flex bg-gray-100 dark:bg-[#112240] rounded-xl p-1 gap-1">
              <button onClick={() => setActiveTab('staff')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'staff' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
                Staff
              </button>
              <button onClick={() => setActiveTab('schedule')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'schedule' ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700'}`}>
                <Calendar size={14} className="inline mr-1" /> Schedule
              </button>
            </div>
          )}
          {hasPermission('export_reports') && (
            <div className="flex items-center gap-2">
              <div className="relative group/xlsx">
                <button onClick={downloadStaffXLSX} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-200 transition-colors">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                </button>
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-[10px] px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover/xlsx:opacity-100 transition-opacity pointer-events-none">Download Excel</div>
              </div>
              <div className="relative group/pdf">
                <button onClick={downloadStaffPDF} className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6M9 17h3"/></svg>
                </button>
                <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-navy-900 text-white text-[10px] px-2 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover/pdf:opacity-100 transition-opacity pointer-events-none">Download PDF</div>
              </div>
            </div>
          )}
          {hasPermission('manage_staff') && (
            <button onClick={() => setShowNewForm(true)} className="btn-primary flex items-center gap-2">
              <Plus size={15} /> New Staff
            </button>
          )}
        </div>
      </div>

      {activeTab === 'schedule' && <ShiftSchedule />}

      {activeTab === 'staff' && <>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9 py-2 text-sm" placeholder="Search staff..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input py-2 text-sm w-36" value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="all">All Roles</option>
          <option value="admin">Admin</option>
          <option value="qc">QC</option>
          <option value="artist">Artist</option>
          <option value="selfqc">Self QC</option>
          <option value="hr">HR</option>
        </select>
        <select className="input py-2 text-sm w-36" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="active">Active</option>
          <option value="all">All Status</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="card overflow-hidden">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-gray-50 dark:border-[#1a3050]">
              <div className="skeleton w-8 h-8 rounded-lg flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 w-32" />
                <div className="skeleton h-3 w-20" />
              </div>
              <div className="skeleton h-6 w-16 rounded-full hidden sm:block" />
              <div className="skeleton h-6 w-16 rounded-full hidden sm:block" />
            </div>
          ))}
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Name</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Username</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Role</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Status</th>
                <th className="text-left px-6 py-3.5 text-xs font-semibold text-gray-400 uppercase tracking-wide hidden sm:table-cell">Last Login</th>
                {hasPermission('export_reports') && <th className="px-4 py-3.5"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(member => (
                <tr key={member.id} onClick={() => setSelected(member)} className="hover:bg-orange-50/40 transition-colors cursor-pointer group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="relative w-8 h-8 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0 cursor-pointer"
                        onMouseEnter={e => {
                          if (!window.matchMedia('(hover: hover)').matches) return
                          e.stopPropagation()
                          const pos = { x: e.clientX, y: e.clientY }
                          artistHoverTimeout.current = setTimeout(async () => {
                            setArtistHoverPos(pos)
                            setHoveredArtist({ name: member.name, id: member.id })
                            try {
                              const res = await api.get(`/staff/${member.id}/quick-stats`)
                              setArtistStats(res.data)
                            } catch {}
                          }, 400)
                        }}
                        onMouseLeave={e => {
                          e.stopPropagation()
                          if (artistHoverTimeout.current) clearTimeout(artistHoverTimeout.current)
                          setHoveredArtist(null)
                          setArtistStats(null)
                        }}
                      >
                        <span className="text-navy-600 font-semibold text-xs">{member.name[0]}</span>
                        {['artist', 'selfqc'].includes(member.roleName) && (
                          <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                            artistStatus[member.id] === 'running' ? 'bg-orange-400' :
                            artistStatus[member.id] === 'busy' ? 'bg-red-400' : 'bg-green-400'
                          }`} title={
                            artistStatus[member.id] === 'running' ? 'Timer running' :
                            artistStatus[member.id] === 'busy' ? 'Has jobs, timer off' : 'Free'
                          } />
                        )}
                      </div>
                      <span className="font-medium text-navy-800 group-hover:text-orange-500 transition-colors">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-mono text-xs text-orange-500 hidden sm:table-cell">
                    {member.username ? `@${member.username}` : '—'}
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-navy-50 text-navy-600 px-2.5 py-1 rounded-full text-xs font-medium">{member.roleLabel}</span>
                  </td>
                  <td className="px-6 py-4 hidden sm:table-cell">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${member.status === 'active' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                      {member.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-xs hidden sm:table-cell">
                    {member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleDateString() : 'Never'}
                  </td>
                  {hasPermission('export_reports') && (
                    <td className="px-4 py-4" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button title="Download Excel" onClick={async () => {
                          let hrData: any = {}
                          try { const r = await api.get(`/staff/${member.id}/hr-info`); hrData = r.data } catch {}
                          const rows = [{
                            'Employee ID': hrData.employeeId || '',
                            Name: member.name,
                            Username: member.username || '',
                            Email: member.email || '',
                            'Personal Email': hrData.personalEmail || '',
                            Role: member.roleName || '',
                            Status: member.status || '',
                            Phone: member.phone || '',
                            'Alt Phone': hrData.altPhoneNumber || '',
                            Gender: hrData.gender || '',
                            'Blood Group': hrData.bloodGroup || '',
                            'Date of Birth': member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString('en-IN') : '',
                            'Joining Date': member.joiningDate ? new Date(member.joiningDate).toLocaleDateString('en-IN') : '',
                            'Last Login': member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleDateString('en-IN') : 'Never',
                            'Emergency Contact': hrData.emergencyContactName || '',
                            'Emergency Relationship': hrData.emergencyRelationship || '',
                            'Emergency Phone': hrData.emergencyContactPhone || '',
                            'Bank Account': hrData.bankAccountNumber || '',
                            'IFSC Code': hrData.bankIfscCode || '',
                            'Branch Name': hrData.bankBranchName || '',
                          }]
                          const ws = XLSX.utils.json_to_sheet(rows)
                          const wb = XLSX.utils.book_new()
                          XLSX.utils.book_append_sheet(wb, ws, 'Staff')
                          XLSX.writeFile(wb, `${member.name.replace(/\s+/g, '_')}_Staff_Card.xlsx`)
                        }} className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-green-600 hover:border-green-200 transition-colors">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                        </button>
                        <button title="Download PDF" onClick={async () => {
                          const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
                          const pageW = doc.internal.pageSize.getWidth()
                          doc.setFillColor(10, 22, 40)
                          doc.rect(0, 0, pageW, 22, 'F')
                          try {
                            const logoRes = await fetch('/Logo_Black Bg_Horizontal.png')
                            const blob = await logoRes.blob()
                            const logoBase64 = await new Promise<string>(resolve => {
                              const reader = new FileReader()
                              reader.onloadend = () => resolve(reader.result as string)
                              reader.readAsDataURL(blob)
                            })
                            doc.addImage(logoBase64, 'PNG', 10, 6, 36, 36 * (50 / 201))
                          } catch {}
                          doc.setTextColor(255, 255, 255)
                          doc.setFontSize(12)
                          doc.setFont('helvetica', 'bold')
                          doc.text('Staff Card', pageW - 10, 10, { align: 'right' })
                          doc.setFontSize(8)
                          doc.setFont('helvetica', 'normal')
                          doc.setTextColor(180, 180, 180)
                          doc.text(`Generated ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`, pageW - 10, 16, { align: 'right' })

                          let hrData: any = {}
                          try { const r = await api.get(`/staff/${member.id}/hr-info`); hrData = r.data } catch {}

                          autoTable(doc, {
                            startY: 28,
                            head: [['Field', 'Details']],
                            body: [
                              ['Name', member.name],
                              ['Employee ID', hrData.employeeId || '—'],
                              ['Username', member.username || '—'],
                              ['Email', member.email || '—'],
                              ['Role', member.roleLabel || member.roleName || '—'],
                              ['Status', member.status || '—'],
                              ['Phone', member.phone || '—'],
                              ['Personal Email', hrData.personalEmail || '—'],
                              ['Alt Phone', hrData.altPhoneNumber || '—'],
                              ['Gender', hrData.gender || '—'],
                              ['Blood Group', hrData.bloodGroup || '—'],
                              ['Date of Birth', member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString('en-IN') : '—'],
                              ['Joining Date', member.joiningDate ? new Date(member.joiningDate).toLocaleDateString('en-IN') : '—'],
                              ['Last Login', member.lastLoginAt ? new Date(member.lastLoginAt).toLocaleDateString('en-IN') : 'Never'],
                            ],
                            styles: { fontSize: 10, cellPadding: 4 },
                            headStyles: { fillColor: [10, 22, 40], textColor: 255 },
                            columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold', textColor: [80, 80, 80] } },
                            margin: { left: 10, right: 10 },
                            theme: 'grid'
                          })

                          autoTable(doc, {
                            startY: (doc as any).lastAutoTable.finalY + 6,
                            head: [['Emergency Contact', '']],
                            body: [
                              ['Contact Name', hrData.emergencyContactName || '—'],
                              ['Relationship', hrData.emergencyRelationship || '—'],
                              ['Contact Phone', hrData.emergencyContactPhone || '—'],
                            ],
                            styles: { fontSize: 10, cellPadding: 4 },
                            headStyles: { fillColor: [30, 58, 95], textColor: 255 },
                            columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold', textColor: [80, 80, 80] } },
                            margin: { left: 10, right: 10 },
                            theme: 'grid'
                          })

                          autoTable(doc, {
                            startY: (doc as any).lastAutoTable.finalY + 6,
                            head: [['Bank Details', '']],
                            body: [
                              ['Account Number', hrData.bankAccountNumber || '—'],
                              ['IFSC Code', hrData.bankIfscCode || '—'],
                              ['Branch Name', hrData.bankBranchName || '—'],
                            ],
                            styles: { fontSize: 10, cellPadding: 4 },
                            headStyles: { fillColor: [30, 58, 95], textColor: 255 },
                            columnStyles: { 0: { cellWidth: 50, fontStyle: 'bold', textColor: [80, 80, 80] } },
                            margin: { left: 10, right: 10 },
                            theme: 'grid'
                          })

                          doc.setFontSize(8)
                          doc.setTextColor(150, 150, 150)
                          doc.text('Inkcredible Studios · Confidential · For internal use only', pageW / 2, 287, { align: 'center' })
                          doc.save(`${member.name.replace(/\s+/g, '_')}_Staff_Card.pdf`)
                        }} className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M9 13h6M9 17h3"/></svg>
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Artist hover card */}
      {hoveredArtist && createPortal(
        <div
          className="fixed z-[9999] w-64 bg-white dark:bg-[#1c3a5e] border border-gray-100 dark:border-[#1e3a5f] rounded-2xl shadow-2xl p-4 pointer-events-none"
          style={{
            left: artistHoverPos.x > window.innerWidth - 280 ? artistHoverPos.x - 270 : artistHoverPos.x + 12,
            top: artistHoverPos.y > window.innerHeight - 200 ? artistHoverPos.y - 200 : artistHoverPos.y + 12,
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-navy-600 font-bold text-sm">{hoveredArtist.name[0]}</span>
            </div>
            <div>
              <p className="font-semibold text-navy-900 dark:text-white text-sm">{hoveredArtist.name}</p>
              {artistStats && <p className="text-xs text-gray-400 capitalize">{artistStats.role}</p>}
            </div>
            {artistStats && (
              <span className={`ml-auto w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                artistStats.isRunning ? 'bg-orange-400' : artistStats.activeJob ? 'bg-red-400' : 'bg-green-400'
              }`} />
            )}
          </div>
          {artistStats ? (
            <div className="space-y-2">
              {artistStats.activeJob && (
                <div className="bg-gray-50 dark:bg-[#112240] rounded-xl px-3 py-2">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Current Job</p>
                  <p className="text-xs font-medium text-navy-800 dark:text-white truncate">{artistStats.activeJob.title}</p>
                  <p className="text-[10px] font-mono text-gray-400">{artistStats.activeJob.jobCode} · {artistStats.activeJob.status}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center">
                  <p className="text-lg font-bold text-orange-500">{artistStats.todayCredits}</p>
                  <p className="text-[10px] text-gray-400">Today's Credits</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-navy-800 dark:text-white">{artistStats.efficiencyPct}%</p>
                  <p className="text-[10px] text-gray-400">Efficiency</p>
                </div>
              </div>
              <div className="w-full h-1 bg-gray-100 dark:bg-[#1a3050] rounded-full overflow-hidden">
                <div className={`h-1 rounded-full transition-all ${
                  artistStats.efficiencyPct >= 100 ? 'bg-yellow-400' :
                  artistStats.efficiencyPct >= 80 ? 'bg-green-400' :
                  artistStats.efficiencyPct >= 50 ? 'bg-orange-400' : 'bg-red-400'
                }`} style={{ width: `${Math.min(100, artistStats.efficiencyPct)}%` }} />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="skeleton h-12 rounded-xl" />
              <div className="grid grid-cols-2 gap-2">
                <div className="skeleton h-10 rounded-xl" />
                <div className="skeleton h-10 rounded-xl" />
              </div>
            </div>
          )}
        </div>,
        document.body
      )}

      </>}

      {selected && createPortal(
        <StaffDetailModal
          member={selected}
          onClose={() => setSelected(null)}
          onDeleted={fetchStaff}
          onUpdated={() => { fetchStaff(); setSelected(null) }}
          currentUser={user}
        />,
        document.body
      )}
      {showNewForm && createPortal(<NewStaffModal onClose={() => setShowNewForm(false)} onCreated={fetchStaff} currentUser={user} />, document.body)}
    </div>
  )
}